import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api, { errMsg } from '@/lib/api';
import { getLocalSales } from '@/lib/offlineDb';
import { money, money2, fmtDate } from '@/lib/helpers';
import { toast } from 'sonner';
import { Printer, Share2, MessageCircle, Undo2, Loader2, CheckCircle2, CloudOff, Pencil, Ban, RefreshCw, Store } from 'lucide-react';
import { CustomFieldsView } from '@/components/CustomFieldsView';
import { templateStyle } from '@/lib/receiptTemplates';

export default function InvoiceView() {
  const { invoiceNo } = useParams();
  const navigate = useNavigate();
  const { stores, user, settings } = useApp();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLocal = invoiceNo.startsWith('local-');
  const canManage = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const isOwner = user?.role === 'OWNER';
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const doVoid = async () => {
    if (!voidReason.trim()) { toast.error('Please enter a reason.'); return; }
    setVoiding(true);
    try {
      const r = await api.post(`/sales/${sale.invoice_no}/void`, { reason: voidReason.trim() });
      toast.success(`Bill voided${r.data.data.restored ? ` · ${r.data.data.restored} item(s) returned to stock` : ''}`);
      setVoidOpen(false); navigate('/sales-history');
    } catch (e) { toast.error(errMsg(e)); } finally { setVoiding(false); }
  };
  const [storeOpen, setStoreOpen] = useState(false);
  const [storing, setStoring] = useState(false);
  const [newStore, setNewStore] = useState('');
  const openStoreDialog = () => {
    const other = stores.find((s) => s.id !== sale?.store_id);
    setNewStore(other ? other.id : '');
    setStoreOpen(true);
  };
  const doStoreChange = async () => {
    if (!newStore) { toast.error('Please choose a destination store.'); return; }
    if (storing) return; // guard against double-submit
    setStoring(true);
    try {
      const r = await api.post('/sales/bulk-update-store', { invoices: [sale.invoice_no], store_id: newStore });
      const { updated, skipped } = r.data.data;
      if (updated) { toast.success('Selling store updated.'); }
      else { toast.info(skipped ? 'No change — already recorded under that store.' : 'No change applied.'); }
      setStoreOpen(false);
      await loadSale();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setStoring(false); }
  };
  const branding = settings?.branding || {};
  const rc = settings?.receipt || {};
  const showLogoOnInvoice = (branding.showOnInvoice ?? true) && (rc.show_logo ?? true);
  const invLogoSize = { small: 'h-8', medium: 'h-12', large: 'h-16' }[branding.logoSize || 'medium'];
  const tpl = templateStyle(rc.template);
  const divCls = tpl.divider === 'dashed' ? 'border-t border-dashed border-border' : 'border-t border-border';
  const brandCls = tpl.brandSize === 'xl' ? 'text-3xl' : tpl.brandSize === 'md' ? 'text-xl' : 'text-2xl';
  const centered = tpl.brandAlign === 'center';

  const [undoing, setUndoing] = useState('');
  const loadSale = async () => {
    try {
      if (isLocal) {
        const txn = invoiceNo.replace('local-', '');
        const all = await getLocalSales();
        setSale(all.find((s) => s.client_txn_id === txn));
      } else {
        const r = await api.get(`/sales/${invoiceNo}`);
        setSale(r.data.data);
      }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const undoExchange = async (exNo) => {
    if (!window.confirm('Reverse this exchange and restore the original item and totals?')) return;
    setUndoing(exNo);
    try {
      await api.post(`/exchanges/${exNo}/undo`);
      toast.success('Exchange reversed — invoice restored.');
      await loadSale();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUndoing(''); }
  };

  useEffect(() => {
    (async () => { setLoading(true); await loadSale(); setLoading(false); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNo, isLocal]);

  const storeName = sale?.store_name || stores.find((s) => s.id === sale?.store_id)?.name || '';
  const undoneEx = new Set((sale?.history || []).filter((h) => h.event === 'EXCHANGE_UNDO').map((h) => h.exchange_no));
  const exchanges = (sale?.history || []).filter((h) => h.event === 'EXCHANGE' && !undoneEx.has(h.exchange_no));

  const shareText = () => {
    if (!sale) return '';
    const lines = sale.items.map((i) => `${i.product} (${i.barcode}) - ${money(i.final)}`).join('%0A');
    return `*SHABZ CLOSET*%0AInvoice ${sale.invoice_no}%0A${storeName}%0A%0A${lines}%0A%0ATotal: ${money(sale.net)}%0AThank you!`;
  };
  const whatsapp = () => {
    const phone = sale?.customer?.phone ? `91${sale.customer.phone}` : '';
    window.open(`https://wa.me/${phone}?text=${shareText()}`, '_blank');
  };
  const share = async () => {
    const text = decodeURIComponent(shareText().replace(/%0A/g, '\n'));
    if (navigator.share) { try { await navigator.share({ title: sale.invoice_no, text }); } catch {} }
    else { navigator.clipboard?.writeText(text); toast.success('Invoice copied to clipboard'); }
  };

  if (loading) return <Page><TopBar title="Invoice" back showStore={false} /><div className="flex justify-center pt-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></Page>;
  if (!sale) return <Page><TopBar title="Invoice" back showStore={false} /><p className="p-6 text-center text-muted-foreground">Invoice not found.</p></Page>;

  return (
    <Page>
      <TopBar title="Invoice" back showStore={false} />
      <div className="px-4 pt-4">
        <div className={`print-area rounded-2xl border border-border bg-card p-5 ${tpl.mono ? 'font-mono' : ''}`} data-testid="invoice-card">
          {centered ? (
            <div className="flex flex-col items-center text-center" data-testid="invoice-header-centered">
              {showLogoOnInvoice && branding.logo && (
                <img src={branding.logo} alt="Logo" data-testid="invoice-logo" className={`${invLogoSize} mb-1 w-auto rounded object-contain`} />
              )}
              <h2 className={`font-display font-bold tracking-tight ${brandCls}`} data-testid="invoice-title">{rc.invoice_title || 'SHABZ CLOSET'}</h2>
              {rc.header_note && <p className="text-[11px] text-muted-foreground" data-testid="invoice-header-note">{rc.header_note}</p>}
              <p className="text-xs text-muted-foreground">{storeName}</p>
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <span data-testid="invoice-number" className="font-mono font-semibold text-foreground">{sale.invoice_no}</span>
                <span>·</span><span>{fmtDate(sale.created_at)}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {showLogoOnInvoice && branding.logo && (
                  <img src={branding.logo} alt="Logo" data-testid="invoice-logo"
                    className={`${invLogoSize} w-auto rounded object-contain`} />
                )}
                <div>
                  <h2 className={`font-display font-bold tracking-tight ${brandCls}`} data-testid="invoice-title">{rc.invoice_title || 'SHABZ CLOSET'}</h2>
                  {rc.header_note && <p className="text-[11px] text-muted-foreground" data-testid="invoice-header-note">{rc.header_note}</p>}
                  <p className="text-xs text-muted-foreground">{storeName}</p>
                </div>
              </div>
              <div className="text-right">
                <p data-testid="invoice-number" className="font-mono text-sm font-semibold">{sale.invoice_no}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(sale.created_at)}</p>
              </div>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {isLocal && !sale.synced ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200"><CloudOff className="h-3 w-3" /> Saved offline</span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 ring-1 ring-emerald-200"><CheckCircle2 className="h-3 w-3" /> Completed</span>
            )}
            {sale.edited_by && (
              <span className="no-print inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-800 ring-1 ring-indigo-200" data-testid="invoice-edited-badge">
                <Pencil className="h-3 w-3" /> Edited by {sale.edited_by} · {fmtDate(sale.edited_at)}
              </span>
            )}
          </div>

          {((rc.show_customer !== false && (sale.customer?.name || sale.customer?.phone)) || (rc.show_salesperson !== false && sale.salesperson)) && (
            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {rc.show_customer !== false && (sale.customer?.name || sale.customer?.phone) && (
                <div><p className="text-xs text-muted-foreground">Customer</p>
                  <p className="font-medium">{sale.customer?.name || '—'}</p>
                  <p className="text-xs text-muted-foreground">{sale.customer?.phone}</p></div>
              )}
              {rc.show_salesperson !== false && (
                <div><p className="text-xs text-muted-foreground">Sold by</p><p className="font-medium">{sale.salesperson}</p></div>
              )}
            </div>
          )}

          <div className={`mt-4 pt-3 ${divCls}`}>
            {sale.items.map((i, idx) => (
              <div key={idx} className="flex items-start justify-between py-1.5 text-sm">
                <div className="min-w-0">
                  <p className={`truncate font-medium ${i.returned ? 'text-muted-foreground line-through' : ''}`}>{i.product}</p>
                  <p className="font-mono text-xs text-muted-foreground">{i.barcode} {i.size ? `· ${i.size}` : ''}</p>
                  {i.salesperson && i.salesperson !== sale.salesperson && (
                    <p className="text-[11px] text-muted-foreground" data-testid={`invoice-item-soldby-${idx}`}>Sold by: <span className="font-medium text-foreground">{i.salesperson}</span></p>
                  )}
                </div>
                <div className="text-right">
                  {i.discount > 0 && <p className="text-xs text-muted-foreground line-through">{money(i.price)}</p>}
                  <p className="font-mono tabular-nums">{money(i.final)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`mt-3 space-y-1 pt-3 text-sm ${divCls}`}>
            <Row label="Subtotal" value={money2(sale.gross ?? sale.items.reduce((s, i) => s + i.price, 0))} />
            {(sale.item_discount > 0 || sale.bill_discount > 0) && (
              <Row label="Discount" value={`- ${money2((sale.item_discount || 0) + (sale.bill_discount || 0))}`} />
            )}
            {tpl.totalStyle === 'boxed' ? (
              <div className="mt-1 flex items-center justify-between rounded-lg bg-primary px-3 py-2 text-base font-bold text-primary-foreground" data-testid="invoice-total-boxed">
                <span>Total</span><span className="font-mono tabular-nums">{money2(sale.net)}</span>
              </div>
            ) : (
              <div className={`flex justify-between pt-2 text-base font-bold ${divCls}`}>
                <span>Total</span><span className={`font-mono tabular-nums ${tpl.totalStyle === 'accent' && rc.accent_totals !== false ? 'text-primary' : ''}`}>{money2(sale.net)}</span>
              </div>
            )}
            {Number(rc.tax_percent) > 0 && (
              <div className="flex justify-between text-[11px] text-muted-foreground" data-testid="invoice-tax-line">
                <span>{rc.tax_label || 'GST'} ({Number(rc.tax_percent)}%) incl.</span>
                <span className="font-mono tabular-nums">{money2(sale.net - sale.net / (1 + Number(rc.tax_percent) / 100))}</span>
              </div>
            )}
            {rc.show_gst_line !== false && (
              <p className="text-[10px] text-muted-foreground">{rc.gst_line_text || 'Prices are GST inclusive.'}</p>
            )}
          </div>

          {exchanges.length > 0 && (
            <div className={`mt-3 pt-3 ${divCls}`} data-testid="invoice-exchanges">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Exchanges</p>
              {exchanges.map((h, i) => (
                <div key={i} className="mb-1 text-xs" data-testid={`invoice-exchange-${h.exchange_no}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{h.exchange_no}</span>
                    <span className="font-mono tabular-nums">{money2(h.old_net)} → {money2(h.new_net)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-muted-foreground">
                    <span>Returned {h.out} → New {h.in}</span>
                    <span className={`font-mono ${(h.new_net - h.old_net) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {(h.new_net - h.old_net) >= 0 ? 'Customer paid ' : 'Refunded '}{money2(Math.abs(h.new_net - h.old_net))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {sale.can_view_profit && (
            <div className="no-print mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="invoice-profit">
              <div className="flex items-center justify-between">
                <span className="font-medium text-emerald-800">Bill profit</span>
                <span className="font-mono text-base font-bold text-emerald-700" data-testid="invoice-profit-value">{money2(sale.profit)}</span>
              </div>
              <p className="mt-0.5 text-[11px] text-emerald-700/80">Selling {money2(sale.net)} − cost {money2(sale.cost_total)}. Only visible to you (cost-access users).</p>
            </div>
          )}

          <div className={`mt-3 pt-3 text-sm ${divCls}`}>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Payment</p>
            {sale.payments.map((p, i) => (
              <div key={i} className="flex justify-between"><span>{p.method}</span>
                <span className="font-mono tabular-nums">{money2(p.amount)}</span></div>
            ))}
            {sale.outstanding > 0 && <Row label="Outstanding" value={money2(sale.outstanding)} />}
          </div>
          {(sale.due_date || sale.notes) && (
            <div className={`mt-3 pt-3 ${divCls}`} data-testid="invoice-due-notes">
              {sale.due_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Delivery / due</span>
                  <span className="font-medium">{fmtDate(sale.due_date)}{sale.due_cleared ? ' · Cleared' : ''}</span>
                </div>
              )}
              {sale.notes && <p className="mt-1 text-sm"><span className="text-muted-foreground">Notes: </span>{sale.notes}</p>}
            </div>
          )}
          {sale.custom && Object.keys(sale.custom).length > 0 && (
            <div className={`mt-3 pt-3 ${divCls}`}>
              <p className="mb-2 text-xs font-medium text-muted-foreground">Additional details</p>
              <CustomFieldsView defs={settings?.custom_fields} values={sale.custom} idPrefix="invoice-cf" />
            </div>
          )}
          <p className="mt-4 text-center text-xs text-muted-foreground" data-testid="invoice-footer">{rc.footer_text || settings?.footer_note || 'Thank you for shopping with SHABZ CLOSET!'}</p>
        </div>

        {!isLocal && (
          <div className="no-print mt-4 rounded-2xl border border-border bg-card p-4" data-testid="invoice-history">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">History</p>
            <div className="space-y-3">
              {[
                { event: 'CREATED', by: sale.created_by, ts: sale.created_at },
                ...(sale.history || []).filter((h) => h.event === 'EDITED' || h.event === 'EXCHANGE'),
              ].map((h, idx) => (
                <div key={idx} className="flex gap-3">
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${h.event === 'EDITED' ? 'bg-indigo-100 text-indigo-700' : h.event === 'EXCHANGE' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {h.event === 'EDITED' ? <Pencil className="h-4 w-4" /> : h.event === 'EXCHANGE' ? <RefreshCw className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{h.event === 'EDITED' ? 'Edited' : h.event === 'EXCHANGE' ? `Exchange ${h.exchange_no || ''}`.trim() : (h.by === 'import' ? 'Created (imported)' : 'Created')}</p>
                      <div className="flex items-center gap-2">
                        {h.event === 'EXCHANGE' && !undoneEx.has(h.exchange_no) && canManage && (
                          <button onClick={() => undoExchange(h.exchange_no)} disabled={undoing === h.exchange_no} data-testid={`invoice-undo-exchange-${h.exchange_no}`}
                            className="inline-flex items-center gap-1 rounded-md border border-destructive/40 px-2 py-0.5 text-[11px] font-medium text-destructive hover:bg-destructive/10">
                            {undoing === h.exchange_no ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Undo
                          </button>
                        )}
                        {h.event === 'EXCHANGE' && undoneEx.has(h.exchange_no) && <span className="text-[11px] text-muted-foreground">reversed</span>}
                        <p className="text-xs text-muted-foreground">{fmtDate(h.ts)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {h.by ? `by ${h.by}` : ''}
                      {h.event === 'EDITED' && h.old_net != null && ` · total ${money2(h.old_net)} → ${money2(h.new_net)}`}
                      {h.event === 'EDITED' && (h.restored > 0 || h.added > 0) ? ` · ${h.added || 0} added, ${h.restored || 0} removed` : ''}
                      {h.event === 'EXCHANGE' && ` · ${h.out} → ${h.in}${h.old_net != null ? ` · total ${money2(h.old_net)} → ${money2(h.new_net)}` : ''}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="no-print mt-4 grid grid-cols-2 gap-2">
          <Button data-testid="invoice-print" variant="secondary" className="h-11 rounded-xl" onClick={() => window.print()}><Printer className="mr-2 h-4 w-4" />Print / PDF</Button>
          <Button data-testid="invoice-share" variant="secondary" className="h-11 rounded-xl" onClick={share}><Share2 className="mr-2 h-4 w-4" />Share</Button>
          <Button data-testid="invoice-whatsapp" variant="secondary" className="h-11 rounded-xl" onClick={whatsapp}><MessageCircle className="mr-2 h-4 w-4" />WhatsApp</Button>
          {!isLocal && <Button data-testid="invoice-return" variant="secondary" className="h-11 rounded-xl" onClick={() => navigate(`/returns?invoice=${sale.invoice_no}`)}><Undo2 className="mr-2 h-4 w-4" />Return</Button>}
        </div>
        {!isLocal && canManage && (
          <Button data-testid="invoice-edit" variant="secondary" className="no-print mt-2 h-11 w-full rounded-xl" onClick={() => navigate(`/invoice/${sale.invoice_no}/edit`)}>
            <Pencil className="mr-2 h-4 w-4" />Edit Invoice
          </Button>
        )}
        {!isLocal && isOwner && (
          <Button data-testid="invoice-change-store" variant="secondary" className="no-print mt-2 h-11 w-full rounded-xl" onClick={openStoreDialog}>
            <Store className="mr-2 h-4 w-4" />Change Store · {storeName}
          </Button>
        )}
        {!isLocal && isOwner && (
          <Button data-testid="invoice-void" variant="outline" className="no-print mt-2 h-11 w-full rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => { setVoidReason(''); setVoidOpen(true); }}>
            <Ban className="mr-2 h-4 w-4" />Void Bill
          </Button>
        )}
        <Button data-testid="invoice-new-bill" className="no-print mt-2 h-12 w-full rounded-xl" onClick={() => navigate('/billing')}>New Bill</Button>
      </div>

      <AlertDialog open={voidOpen} onOpenChange={(o) => { if (!o) setVoidOpen(false); }}>
        <AlertDialogContent data-testid="void-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Void bill {sale?.invoice_no}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the bill from all reports and returns its item(s) to stock. A copy is
              kept in the audit trail (who, when and why). This cannot be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <Input autoFocus placeholder="Reason (required) e.g. wrong bill, customer cancelled"
              value={voidReason} onChange={(e) => setVoidReason(e.target.value)} data-testid="void-reason" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={voiding}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doVoid} disabled={voiding || !voidReason.trim()} data-testid="void-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {voiding ? 'Voiding…' : 'Void bill'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={storeOpen} onOpenChange={(o) => { if (!storing) setStoreOpen(o); }}>
        <AlertDialogContent data-testid="invoice-change-store-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Change selling store</AlertDialogTitle>
            <AlertDialogDescription>
              Re-file invoice <b>{sale?.invoice_no}</b> under a different store. This corrects where the sale was recorded — the invoice number, items, prices, payments and dues are preserved and nothing is duplicated. The sale, profit, returns and this invoice's stock move to the selected store.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Current store</p>
              <p className="text-sm font-medium" data-testid="invoice-change-store-current">{storeName || '—'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Transfer to</Label>
              <Select value={newStore} onValueChange={setNewStore}>
                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="invoice-change-store-select"><SelectValue placeholder="Select a store" /></SelectTrigger>
                <SelectContent>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id} disabled={s.id === sale?.store_id} data-testid={`invoice-change-store-opt-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={storing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doStoreChange} disabled={storing || !newStore} data-testid="invoice-change-store-confirm">
              {storing ? 'Applying…' : 'Confirm Store Change'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{label}</span>
    <span className="font-mono tabular-nums">{value}</span></div>
);
